import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import type { SubscriberListView } from '@/types/api';

export function AdminSubscriberListsPage() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SubscriberListView | null>(null);

  const reload = () => {
    setLoading(true);
    adminSubscriberListsService
      .list()
      .then(setLists)
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Could not load lists.'))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      await adminSubscriberListsService.create({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success('List created.');
      setName('');
      setDescription('');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create list.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await adminSubscriberListsService.remove(target.id);
      setLists((prev) => prev.filter((l) => l.id !== target.id));
      toast.success('List deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete list.');
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div>
          <div className="or-kicker text-or-orange-600">Admin · Subscribers</div>
          <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
            Lists
          </h1>
          <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
            Group subscribers into lists you can broadcast to. A subscriber belongs to one list at a
            time.
          </p>
        </div>

        {/* Create */}
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap items-end gap-3 rounded-2xl border border-or-slate-200 bg-white p-4"
        >
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
            List name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Newsletter"
              className="h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </label>
          <label className="flex flex-[2] flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
            Description (optional)
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this list for?"
              className="h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </label>
          <Button variant="primary" size="md" type="submit" disabled={!name.trim() || creating}>
            <Plus className="h-4 w-4" aria-hidden />
            {creating ? 'Creating…' : 'Create list'}
          </Button>
        </form>

        {/* List of lists */}
        <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
          {loading && lists.length === 0 ? (
            <div className="px-4 py-12 text-center text-or-slate-500">Loading…</div>
          ) : lists.length === 0 ? (
            <div className="px-4 py-12 text-center text-or-slate-500">No lists yet.</div>
          ) : (
            <ul>
              {lists.map((list) => (
                <li
                  key={list.id}
                  className="flex items-center justify-between border-t border-or-slate-100 px-4 py-3 first:border-t-0"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/subscribers/lists/${list.id}`)}
                    className="flex flex-1 items-center gap-3 text-left"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-or-slate-900">{list.name}</span>
                        {list.isSystem ? <Chip variant="default">System</Chip> : null}
                      </div>
                      {list.description ? (
                        <p className="text-[12px] text-or-slate-500">{list.description}</p>
                      ) : null}
                    </div>
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-[12px] text-or-slate-500">
                      {list.memberCount.toLocaleString()} member{list.memberCount === 1 ? '' : 's'}
                    </span>
                    {!list.isSystem ? (
                      <Button variant="danger" size="sm" onClick={() => setPendingDelete(list)}>
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/subscribers/lists/${list.id}`)}
                      className="text-or-slate-400 hover:text-or-slate-700"
                      aria-label={`Open ${list.name}`}
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this list?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed. Its ${pendingDelete.memberCount} subscriber(s) stay in your audience but become unassigned.`
            : ''
        }
        confirmLabel="Delete list"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />
    </AppShell>
  );
}
